import { useQuery, UseQueryResult } from 'react-query';

import { useAuth } from 'src/hooks';

import {
  getCallDocumentList,
  getCallDocumentPreview,
  showNavigationPreview,
} from 'src/utils/urls';
import { useCallDocStore } from '../store/callDocStore';
import { setCallDocListRequestData } from '../utils/apiHelper/callDocListData';
import { initialState } from '../utils/useCallDocumentFilterReducer';

import { CallDocumentsPreviewType } from '../types/callDocumentPreview';
import { CallDocumentType } from '../types/callDocumentList';

export const noCallDocAccessMsg =
  'You do not have access to this call document.';
const purgedCallDocMsg = 'Call is Purged. File is Not available.';

// call document ids already verified for the current token, to avoid repeated finalizedgrid calls
let verifiedToken: string = '';
const verifiedCallDocIds = new Set<number>();

/* finalizedgrid only returns call documents of the accounts the user has access to */
async function hasCallDocumentAccess(
  token: string,
  tokenType: string,
  callDocumentId: number,
): Promise<boolean> {
  if (verifiedToken !== token) {
    verifiedToken = token;
    verifiedCallDocIds.clear();
  }
  if (verifiedCallDocIds.has(callDocumentId)) return true;

  const requestData: any = setCallDocListRequestData(
    { ...initialState, callId: `${callDocumentId}` },
    undefined,
  );
  requestData.append('start', '0');
  const response = await fetch(getCallDocumentList, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  if (response?.status !== 200) return false;
  const result = await response.json();
  const hasAccess =
    Array.isArray(result?.data) &&
    result.data.some(
      (doc: CallDocumentType) => Number(doc?.Id) === callDocumentId,
    );
  if (hasAccess) verifiedCallDocIds.add(callDocumentId);
  return hasAccess;
}

async function fetchCallDocumentsPreview({
  token,
  tokenType,
  callDocumentId,
  isFromMail,
  navigationParam,
}: TVariables): Promise<any> {
  if (
    isFromMail &&
    !(await hasCallDocumentAccess(token, tokenType, callDocumentId))
  ) {
    throw { message: noCallDocAccessMsg };
  }

  let url: string = '';
  isFromMail === true
    ? (url = showNavigationPreview(callDocumentId, navigationParam))
    : (url = getCallDocumentPreview(callDocumentId));
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `${tokenType} ${token}`,
    },
  });
  if (response?.status === 200) {
    const result = await response.json();
    // prev/next navigation can land on another call document, verify that one too
    if (
      isFromMail &&
      result?.CallId &&
      result.CallId !== callDocumentId &&
      !(await hasCallDocumentAccess(token, tokenType, result.CallId))
    ) {
      throw { message: noCallDocAccessMsg };
    }
    return result;
  } else {
    throw { message: response.statusText };
  }
}

export type TResult = CallDocumentsPreviewType;

export type TVariables = {
  token: string;
  tokenType: string;
  callDocumentId: number;
  isFromMail: boolean;
  navigationParam: string;
};

export type TError = { message: string };

function useGetCallDocumentPreview(
  callDocumentId: number | undefined,
  isFromMail: boolean = false,
  navigationParam: string = '',
): UseQueryResult<TResult, TError> {
  const { token, tokenType } = useAuth();
  return useQuery(
    'get_call_documents_preview',
    async () => {
      if (callDocumentId) {
        const result = await fetchCallDocumentsPreview({
          token,
          tokenType,
          callDocumentId,
          isFromMail,
          navigationParam,
        });

        return result;
      }
    },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: false,
      // retrying won't grant access, it only repeats the finalizedgrid call
      retry: (failureCount, err) =>
        err?.message !== noCallDocAccessMsg && failureCount < 3,
      onSuccess: () => {
        if (isFromMail) {
          useCallDocStore.getState().setShowDocPreviewModal(true);
          useCallDocStore.getState().setIsPrevCallDocAvailable(true);
          useCallDocStore.getState().setIsNextCallDocAvailable(true);
        }
      },
      onError: err => {
        if (isFromMail) {
          useCallDocStore.getState().setShowDocPreviewModal(false);
          if (
            err.message === purgedCallDocMsg ||
            err.message === noCallDocAccessMsg
          ) {
            useCallDocStore.getState().setConfirmationMessage(err.message);
            useCallDocStore.getState().setShowConfirmation(true);
          } else {
            useCallDocStore.getState().setShowDocPreviewModal(true);
          }
          if (err.message === 'No previous call document found') {
            useCallDocStore.getState().setIsPrevCallDocAvailable(false);
          }
          if (err.message === 'No next call document found') {
            useCallDocStore.getState().setIsNextCallDocAvailable(false);
          }
        }
      },
    },
  );
}

export { useGetCallDocumentPreview };
